import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { format, parse } from 'date-fns';
import { fetchDealsList, fetchDealOfTheDay } from '../../api';
import { CURRENCY, LOCALE, MARKET } from '../../constants';
import { DEALS_PER_PAGE } from '../../constants/constants';
import { getCheckedCheckboxes, parseDate } from '../../utilities/helpers';
import { pushEvent, pushFbEvent } from '../../utilities/analytics';
import { sendSearchPerformed } from '../../utilities/sendSearchPerformed';
import api from '../../api';
import {
  NO_OFFERS_EVENT,
  LOAD_OFFERS_EVENT,
  LOAD_MORE_DEALS_EVENT,
  SEARCH_OUT_OF_CACHE_NIGHTS,
  DOTD_LOADED,
} from '../../constants/analyticEvents';
import { getParamFromUrl } from '../../utilities/urlResolver';
import abTesting from '../../services/abTesting';

const callEvents = (data, userProfile) => {
  let minOfferPrice = null;
  if (data.offers && data.offers.length) {
    const minOffer = data.offers.reduce((prev, curr) => prev.offer.price < curr.offer.price ? prev : curr);
    if (minOffer && minOffer.offer) {
      const { capacity_from_filter } = minOffer.offer;
      let kids = capacity_from_filter.child;
      const babies = capacity_from_filter.childAges.filter((kid) => kid < 2).length || 0;
      kids = kids - babies; // calculate price per person without babies
      const minPricePerPerson = Math.ceil(minOffer.offer.price / (capacity_from_filter.adult + kids));
      minOfferPrice = minPricePerPerson;
      window.minOfferPricePP = minPricePerPerson;
    }
  }
  sendSearchPerformed(minOfferPrice);
  if (data.offers.length === 0) {
    return pushEvent(NO_OFFERS_EVENT);
  }
  pushEvent(LOAD_OFFERS_EVENT, { offers: data.offers, userProfile: userProfile?.searchProfileData });
  if (data.facebook_product_id) {
    pushFbEvent(data.facebook_product_id);
  }
};

const initialState = {
  pending: true,
  loadMorePending: false,
  nextDeals: [],
  error: false,
  dealsList: [],
  pagination: {},
  userProfileData: {},
  bestOutOf: {},
  dotdPending: !!abTesting.dealOfTheDay,
  dealOfTheDay: [],
};

export const fetchDealsListData = createAsyncThunk(
  'fetchDealsList',
  async (loadAllDeals, { getState, signal }) => {
    const { mainFilter, secondaryFilter, onboarding } = getState();
    const { style } = onboarding?.formV2;
    const request = prepareSearchRequest({
      mainFilter: mainFilter.searchEngine,
      secondaryFilter: secondaryFilter.data
    });

    const startTime = Date.now();
    const userResponse = await api.getUserProfile(request, style);
    const userProfileData = userResponse?.data;
    const response = await fetchDealsList(request, loadAllDeals, userProfileData, signal, startTime, 'fetchDealsList');
    callEvents(response, userProfileData);

    if (response && response.offers && !response.offers.length) {
      const { when, where } = mainFilter.searchEngine;
      const selectedDestinations = where?.destinations.filter(
        ({ active }) => active
      );
      if (
        selectedDestinations?.length > 0 &&
        ((when?.minNights < selectedDestinations[0]?.min_nights &&
          when?.maxNights < selectedDestinations[0]?.min_nights) ||
          selectedDestinations[0]?.max_nights < when?.maxNights)
      ) {
        pushEvent(SEARCH_OUT_OF_CACHE_NIGHTS, {
          destinationNights: {
            min: selectedDestinations[0]?.min_nights,
            max: selectedDestinations[0]?.max_nights,
          },
          selectedNights: { min: when?.minNights, max: when?.maxNights },
          selectedDestinations: selectedDestinations,
        });
      }
    }

    return { userProfileData, dealsListData: response };
  }
);

export const fetchDealOfTheDayData = createAsyncThunk(
  'fetchDealOfTheDay',
  async (params, { getState, signal }) => {
    const { mainFilter, secondaryFilter } = getState();
    const request = prepareSearchRequest({
      mainFilter: mainFilter.searchEngine,
      secondaryFilter: secondaryFilter.data
    });

    const response = await fetchDealOfTheDay(request, signal);

    if (response?.offers?.length) {
      pushEvent(DOTD_LOADED, { offers: response.offers });
    }

    return { dealOfTheDayData: response };
  }
);

export const loadMoreDeals = createAsyncThunk(
  'loadMoreDeals',
  async (params, thunkAPI) => {
    const { mainFilter, secondaryFilter, dealsList } = thunkAPI.getState();

    const request = prepareSearchRequest({
      mainFilter: mainFilter.searchEngine,
      secondaryFilter: secondaryFilter.data,
      pagination: dealsList.pagination
    });

    const startTime = Date.now();

    const userResponse = await api.getUserProfile(request);
    const userProfileData = userResponse?.data

    const response = await fetchDealsList(request, false, userProfileData, false, startTime, 'loadMoreDeals');

    if (response.offers.length === 0) {
      return pushEvent(NO_OFFERS_EVENT);
    }
    pushEvent(LOAD_MORE_DEALS_EVENT, response.offers);

    return {userProfileData, dealsListData: response };
  }
);

export const dealsListSlice = createSlice({
  name: 'dealsList',
  initialState,
  reducers: {
    clearDeals: (state) => {
      return {
        ...state,
        dealsList: [],
        pending: true,
      };
    },
    updateDeals: (state) => {
      return {
        ...state,
        dealsList: [...state.dealsList, ...state.nextDeals],
        pending: false,
        nextDeals: [],
      };
    },
    clearNextDeals: (state) => {
      state.nextDeals = [];
    },
    saveDealsFromPreFetch: (state, action) => {
      state.dealsList = action.payload.deals;
      state.pagination = action.payload.pagination;
      state.nextDeals = [];
      state.pending = false;
      state.error = false;
    },
    setDealsPending: (state, action) => {
      state.pending = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealsListData.pending, (state) => {
        state.pending = true;
      })
      .addCase(fetchDealsListData.fulfilled, (state, action) => {
        const {userProfileData, dealsListData } = action.payload;
        return {
          ...state,
          dealsList: dealsListData.offers,
          pagination: dealsListData.pagination,
          pending: false,
          error: false,
          userProfileData,
          bestOutOf: dealsListData.bestOutOf
        };
      })
      .addCase(fetchDealsListData.rejected, (state, action) => {
        state.dealsList = [];
        state.pending = action.meta.aborted;
        state.error = !action.meta.aborted;
      })
      .addCase(loadMoreDeals.pending, (state) => {
        state.loadMorePending = true;
      })
      .addCase(loadMoreDeals.fulfilled, (state, action) => {
        const {userProfileData, dealsListData } = action.payload
        return {
          ...state,
          dealsList: state.dealsList,
          nextDeals: dealsListData.offers,
          pagination: dealsListData.pagination,
          loadMorePending: false,
          error: false,
          pending: false,
          userProfileData,
          bestOutOf: dealsListData.bestOutOf
        };
      })
      .addCase(fetchDealOfTheDayData.pending, (state) => {
        state.dotdPending = true;
      })
      .addCase(fetchDealOfTheDayData.fulfilled, (state, action) => {
        const { dealOfTheDayData } = action.payload;
        return {
          ...state,
          dealOfTheDay: dealOfTheDayData.offers,
          dotdPending: false,
        };
      })
      .addCase(fetchDealOfTheDayData.rejected, (state, action) => {
        state.dealOfTheDay = [];
        state.dotdPending = action.meta.aborted;
      })
  },
});

export const {
  clearDeals,
  updateDeals,
  clearNextDeals,
  saveDealsFromPreFetch,
  setDealsPending,
} = dealsListSlice.actions;

export const prepareSearchRequest = ({
  mainFilter,
  secondaryFilter,
  pagination = {},
  hotel
}) => {
  const { fromwhere, what, where, budget = [], when, who } = mainFilter;
  const { sort, stars, board, duration, freeCancellation, stops, facilities, luggage, flex } =
    secondaryFilter;
  const activeSort = sort.find((item) => item.active);
  // const selectedDeparture = fromwhere.find((item) => item.active) || fromwhere[0];
  let selectedDepartures = fromwhere.filter((item) => item.active).map(dep => dep.id);
  if (!selectedDepartures.length) {
    selectedDepartures = fromwhere.filter((item) => item.ip_selected).map(dep => dep.id);
  }
  if (!selectedDepartures.length) {
    selectedDepartures = [fromwhere[0].id];
  }

  const formattedWhen = () => {
    if (when.type === 'month') {
      const selectedMonths = Object.values(when.initial.flexible.months).filter(
        (month) =>
          when.months.some(
            (item) =>
              format(parseDate(month.start, 'dd/MM/yyyy'), 'M') === `${item}`
          )
      );

      return {
        months: {
          periods: selectedMonths,
          min: Math.min(...when.nights) || when.initial.flexible.min,
          max: Math.max(...when.nights) || when.initial.flexible.max,
          weekends_only: when.weekendsOnly,
          nights: when.nights,
        },
      };
    }

    if (when.type === 'range') {
      return {
        flexible: {
          start: when.startDate || undefined,
          end: when.endDate || undefined,
          min: Math.min(...when.nights) || when.minNights,
          max: Math.max(...when.nights) || when.maxNights,
          weekends_only: when.weekendsOnly,
          nights: when.nights,
        },
      };
    }

    if (when.type === 'specific') {
      const start = when.startDate || undefined;
      const end = when.endDate || undefined;
      // : format(add(parse(start, 'dd/MM/yyyy', new Date()), { days: 1 }), 'dd/MM/yyyy', new Date())

      return {
        specific: {
          start,
          end,
        },
      };
    }

    return undefined;
  };

  const getDestinations = () => {
    let selectedDestinations = where.destinations;

    if (
      what.some((item) => item.active) &&
      selectedDestinations.filter((item) => item.active).length === 0
    ) {
      const whatTypes = what.filter(({ active }) => active).map(({ id }) => id);

      const filterByType = (data, whatTypes) =>
        data.map((item) => {
          if (item.types === '' || item.types.length === 0)
            return { ...item, hideByType: true };
          return item.types.filter((type) => whatTypes.includes(type)).length >
            0
            ? { ...item, hideByType: false }
            : { ...item, hideByType: true };
        });

      const filterByEachType = (data) =>
        whatTypes.map((id) => {
          return filterByType(data, [id]).filter(
            ({ hideByType }) => !hideByType
          ).length;
        });

      const destinationsForEachType = filterByEachType(selectedDestinations);
      const isEmptyDestinationByType = destinationsForEachType.includes(0);

      if (!isEmptyDestinationByType) {
        selectedDestinations = where.destinations.filter(({ types }) => {
          if (types === '' || types.length === 0) return false;
          return types.filter((type) => whatTypes.includes(type)).length > 0;
        });
      }

      if (
        !selectedDestinations.some((item) => item.active) &&
        !isEmptyDestinationByType
      ) {
        selectedDestinations = selectedDestinations.map((item) => ({
          ...item,
          active: true,
        }));
      }
    }
    const whereSelected = selectedDestinations.filter((item) => item.active);

    const whereTxt = selectedDestinations
      .filter((item) => item.active)
      .map((item) => (item.name_en || item.value));

    return {
      whereId: getCheckedCheckboxes(selectedDestinations),
      whereName: whereTxt,
      whereSelected,
    };
  };

  const formattedWhat = () => {
    const activeWhat = what
      .filter((item) => item.active && item.id)
      .map((filteredItem) => filteredItem.id);
    const activeWhatTxt = what
      .filter((item) => item.active && item.id)
      .map((filteredItem) => filteredItem.value);
    const activeWhatTxtEn = what
      .filter((item) => item.active && item.id)
      .map((filteredItem) => filteredItem.value_en);
    return { whatId: activeWhat, whatName: activeWhatTxt, whatNameEn: activeWhatTxtEn };
  };

  sessionStorage.setItem('whereSelected', JSON.stringify(getDestinations().whereSelected)); // this data is needed for analytics

  const formattedLuggage = {};
  luggage.forEach(bag => {
    if (bag.value) {
      formattedLuggage[bag.value] = bag.active;
    }
  });

  const request = {
    locale: LOCALE,
    currency: CURRENCY,
    fromwhere: selectedDepartures,
    engine: {
      market: MARKET,
      where: getDestinations().whereId,
      when: formattedWhen(),
      who: {
        adult: who.adults,
        child: who.children,
        room: who.rooms,
        childAges: who.childAges,
      },
      what: formattedWhat().whatId,
      whereTxt: getDestinations().whereName,
      whatTxt: formattedWhat().whatNameEn,
      destinationGroups: where.destinationsGroups.filter(
        (destGr) => destGr.active
      ),
    },
    filters: {
      rating: stars.filter((item) => item.active).map((item) => item.value),
      stops: stops.filter((item) => item.active).map((item) => item.value),
      refundable: freeCancellation,
      board: board
        .filter((item) => item.active)
        .map((item) => item.value.toUpperCase()),
      amenities: facilities
        .filter((item) => item.active)
        .map((item) => item.value),
      amenitiesTxt: facilities
        .filter((item) => item.active)
        .map((item) => item.label),
      luggage: formattedLuggage,
      flex: !!(flex && flex.active),
    },
    sort: {
      [activeSort.label]: activeSort.value,
    },
    limit: DEALS_PER_PAGE,
    offset: pagination.current_offers_page * DEALS_PER_PAGE || 0,
  };

  if (budget.min !== budget.initial.min || budget.max !== budget.initial.max) {
    request.engine.budget = {
      min: budget.min,
      max: budget.max,
    };
  }

  if (duration.maxTime !== duration.activeTime) {
    request.filters.flight_duration = duration.activeTime;
  }

  if (getParamFromUrl('pr_hotelId') || getParamFromUrl('hotelId')) {
    request.specificHotel = {
      hotel_id: getParamFromUrl('pr_hotelId') || getParamFromUrl('hotelId')
    };
    if ((getParamFromUrl('pr_startDate') && getParamFromUrl('pr_endDate')) || (getParamFromUrl('startDate') && getParamFromUrl('endDate'))) {
      request.specificHotel.when = {
        specific: {
          start: format(parse((getParamFromUrl('pr_startDate') || getParamFromUrl('startDate')), 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy'),
          end: format(parse((getParamFromUrl('pr_endDate') || getParamFromUrl('endDate')), 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy')
        }
      };
    }
  }

  if (hotel?.hotelId || hotel?.hfHotelId) {
    request.specificHotel = {
      hotel_id: +hotel.hotelId || +hotel.hfHotelId,
    }
  }

  return request;
};

export const selectDealsList = (state) => state.dealsList;
export const selectDealsListPending = (state) => state.dealsList.pending;
export const selectTotalDealsCount = (state) => state.dealsList.pagination?.total_offers_count || 0;
export const selectUserProfileData = (state) => state.dealsList.userProfileData?.searchProfileData || 0;
export const selectBestOutOfDestinations = (state) => state.dealsList.bestOutOf?.destinations;
export const selectBestOutOfTotal = (state) => state.dealsList.bestOutOf?.total;

export default dealsListSlice.reducer;
